import type { MiraMode } from "@/data/models/MiraMode";
import { ComBoxHumidityUnit } from "./ComBoxHumidityUnit";
import { GeoLocation } from "@/utils/GeocodingService";
import { ComBoxClientConfigInfo } from "./ComBoxClientConfig";
import { HyDryMessagesSettings } from "./HyDryMessagesConfig";
import { Language } from "@/i18n/Languages";
import { 
  ActionButtonOptions, 
  AllDevicesSettings, 
  ClientPdfReportConfig, 
  CompanySettings, 
  DeviceManagerSettings, 
  FinishedProjectsSettings, 
  HyDryDeviceSettings, 
  ImportConfiguration, 
  MeasurementPointPresetPhase,
  NumberOfDays, 
  NumberOfMonths, 
  NumberOfWeeks, 
  ProjectDevicesSettings, 
  ProjectEditingSettings, 
  ProjectManagementSettings, 
  ProjectManagerSettings, 
  ProjectSettingsOptions, 
  ProjectSettingsV2, 
  ProjectSettingsDeprecated, 
  ProjectSupervisorSource, 
  ReportSettings, 
  StandardIdentificationOptions, 
  UserSettings, 
  TableSortingOptions,
  TableSortingOptionsV2,
  ProjectDataExchangeOptions,
  TimeEntriesAsSeparateReport,
  MeasurementProtocolAsSeparateReport,
  ReportFooterText,
  ReportInclusion
} from "./Settings";
import { DEFAULT_LOCALE } from "@/plugins/vuetify";
import { DEFAULT_TIMEZONE } from "@/utils/TimezoneHelper";
import { DocumentsAsSeparateReport, ReportsList, SendReportsOption } from "./Report";

export const MIN_PASSWORD_LENGTH = 6;
export const DEFAULT_COMPANY_NAME = "Beispiel GmbH";

export enum ClientType {
  IRES = "IRES",
  CLIENT = "CLIENT",
}

export enum ProjectManagement {
  NONE = "NONE",
  WEBSITE = "WEBSITE",
  DEFAULT = "DEFAULT",
  OPTIONAL = "OPTIONAL",
  DEVICES_ONLY_WEBSITE = "DEVICES_ONLY_WEBSITE",
  PROJECT_OPTION = "PROJECT_OPTION",
}

export enum ErpIntegration {
  NONE = "NONE",
  PARALLEL = "PARALLEL",
  EXCLUSIVE = "EXCLUSIVE",
}

export interface ClientTestMetadata {
  appVersion?: string | null;
  roomInfoConfigVersion?: string | null;
  userIds?: string[];
}

export interface ClientsInfoOfGroup {
  clientId: string;
  companyName?: string | null;
  clientName?: string | null;
  shortName?: string | null;
  isActive?: boolean;
  /** Mirrored from the client by IRES Cloud since #2610067; absent on entries not changed since. */
  blocked?: boolean;
  deleted?: boolean;
}

export interface ErpSettings {
  integration: ErpIntegration;
}

/**
 * @deprecated UserSettingsDeprecated is deprecated and should not be used.
 */
export interface UserSettingsDeprecated {
  projectSupervisorSource?: ProjectSupervisorSource;
  strictBranchFilters?: boolean;
  keepProjectOwner?: boolean;
}

export interface SyncTarget {
  erpSystems?: string[];
}

interface ComBoxSettings {
  humidityUnit: ComBoxHumidityUnit;
  clientConfig?: ComBoxClientConfigInfo;
}

/**
 * @deprecated DryingParametersDeprecated is deprecated and should not be used.
 */
interface DryingParametersDeprecated {
  defaultDryingDurationInDays: number;
  defaultFollowUpInspectionIntervalInDays: number;
}

/**
 * @deprecated GenericDeviceSettingsDeprecated is deprecated and should not be used.
 */
export interface GenericDeviceSettingsDeprecated {
  newCategoryAllowed: boolean;
  newTemplateAllowed: boolean;
  updateAllowed: boolean;
}

export interface TimeTrackingConfigurations {
  specifyStartEnd: boolean;
  isStartEndMandatory: boolean;
}

export interface DailyTrackingSettings {
  createDailyTracking: boolean;
  sendDailyTracking: boolean;
}

export interface MemberClientsConfiguration {
  defineOwnReportSettings: boolean;
  memberClientsWithOwnReportSettings: string[];
  
  defineOwnRoleSettings: boolean;
  memberClientsWithOwnRoleSettings: string[];

  defineOwnSystemConfiguration: boolean;
  memberClientsWithOwnSystemConfiguration: string[];


  defineOwnUserSettings: boolean;
  memberClientsWithOwnUserSettings: string[];

  defineOwnCompanySettings: boolean;
  memberClientsWithOwnCompanySettings: string[];

  defineOwnHyDryDeviceSettings: boolean;
  memberClientsWithOwnHyDryDeviceSettings: string[];

  defineOwnProjectManagementSettings: boolean;
  memberClientsWithOwnProjectManagementSettings: string[];

  defineOwnAllDevicesSettings: boolean;
  memberClientsWithOwnAllDevicesSettings: string[];

  defineOwnHyDryMessagesSettings: boolean;
  memberClientsWithOwnHyDryMessagesSettings: string[];
}

export enum RenoActivationScope {
  ALL_DRYINGS = "ALL_DRYINGS",
  ACTIVE_AND_NEW_ONLY = "ACTIVE_AND_NEW_ONLY",
}

/**
 * Represents the system configuration.
 */
export interface SystemConfiguration {
  enableAllDevicesManagement: boolean;
  enableDeviceManagementTile: boolean;
  /**
   * KARTE: whether this client has the map features at all (Hans, ticket 2609016).
   *
   * Labelled "Geocodierung" until 2026-09-07 and renamed to "Karte" because it gates a family of
   * map features rather than the geocoding itself: geocoding runs server-side on write whatever
   * this says. The **stored key is deliberately unchanged** — Office, Cloud, Work and Project all
   * read `enableGeoEncoding`, and renaming a persisted field across four products buys nothing a
   * label change does not.
   *
   * Gates [enableTracking], and gates whether Einstellungen Niederlassung offers
   * `enableAddressGeocoding` at all.
   */
  enableGeoEncoding: boolean;
  /**
   * TRACKING: live positions (Hans, ticket 2609016). Requires KARTE.
   *
   * Declared and configurable, with **no consumer yet** — the same posture the address format
   * setting shipped in. Cleared when KARTE is switched off, so it can never stand on without the
   * feature it depends on.
   */
  enableTracking?: boolean;
  enableScheduling: boolean;
  enableTimeTracking: boolean;
  enableVehicleManagement: boolean;
  enableDeviceCheckManagement?: boolean;
  enableAiPredictions?: boolean;
  enableBillingCatalogs?: boolean;
  renoActivationScope?: RenoActivationScope;
  /**
   * Personal chat (Hans, ticket 2610004: "Chat / Messenger aktivieren?"). Absent = off, so every
   * client starts at "Nein" without a migration.
   *
   * Stored as two booleans rather than one four-valued enum: an app build that deserializes an
   * enum it does not know can fail on the whole settings document (the table sorting trap), and a
   * boolean cannot. Office's picker shows the pair as one choice, see `src/utils/ChatMode.ts`.
   * **No consumer yet**; IRES Work's chat is a placeholder.
   */
  enablePersonalChat?: boolean;
  /** Project chat (ticket 2610004). Absent = off; see {@link enablePersonalChat}. */
  enableProjectChat?: boolean;
  /** Who sees Mira (#2610141): Nein / Nur IRES / Ja. Absent = Nur IRES; see `MiraMode.ts`. */
  miraMode?: MiraMode;

  isDefaultSystemConfiguration?: boolean;
}

export interface ClientConfiguration {
  defineOwnAllDevicesSettings: boolean;
  defineOwnCompanySettings: boolean;
  defineOwnHyDryDeviceSettings: boolean;
  defineOwnHyDryMessagesSettings: boolean;
  defineOwnProjectManagementSettings: boolean;
  defineOwnReportSettings: boolean;
  defineOwnRoleSettings: boolean;
  defineOwnSystemConfiguration: boolean;
  defineOwnUserSettings: boolean;
}

/**
 * @deprecated CockpitSettingsDeprecated is deprecated and should not be used.
 */
export interface CockpitSettingsDeprecated {
  cockpitActiveForAllUsers: boolean;
}

export interface Client {
  id: string;
  hasApp?: boolean;
  companyShorthand: string;
  clientType?: ClientType;
  clientName: string;
  shortName?: string | null;

  /** 
   * @deprecated 
   * `reportEmailAddress` is deprecated. When it is set and `dryingReportEmailAddress`, 
   * `damageReportEmailAddress`, and `leakDetectionReportEmailAddress` are unset, its value 
   * is copied to these three variables for backward compatibility.
   */
  reportEmailAddress: string | null;

  /**
   * @deprecated 
   * `dryingReportEmailAddress` is deprecated and should not be used. Please see `ReportSettings`.
   */
  dryingReportEmailAddress?: string | null;
  /**
   * @deprecated 
   * `damageReportEmailAddress` is deprecated and should not be used. Please see `ReportSettings`.
   */
  damageReportEmailAddress?: string | null;
  /**
   * @deprecated 
   * `leakDetectionReportEmailAddress` is deprecated and should not be used. Please see `ReportSettings`.
   */
  leakDetectionReportEmailAddress?: string | null;

  /**
   * @deprecated 
   * `userSettings` is deprecated and should not be used. Please see `UserSettings`.
   */
  userSettings?: UserSettingsDeprecated;
  /**
   * @deprecated 
   * `cockpitSettings` is deprecated and should not be used. Please see `UserSettings`.
   */
  cockpitSettings?: CockpitSettingsDeprecated;

  /**
   * @deprecated 
   * `pdfReportConfig` is deprecated and should not be used. Please see `ReportSettings`.
   */
  pdfReportConfig: ClientPdfReportConfig | null;

  companyName: string;

  /**
   * @deprecated 
   * `logoFile` is deprecated and should not be used. Please see `ReportSettings`.
   */
  logoFile?: string;

  street: string;
  houseNumber: string;

  zip: string;
  city: string;

  /**
   * ISO2 country code of the company address. Optional because it was added after the
   * other address fields, so existing client documents do not carry it.
   */
  country?: string | null;

  /**
   * Position of the company address: geocoded by IRES Cloud's client trigger, or placed
   * by hand on the map, which is recorded as quality `MANUAL` and then never overwritten
   * automatically.
   */
  coordinates?: GeoLocation | null;

  department: string;

  /**
   * @deprecated
   * `minProgramChainDurationInMin` is deprecated and should not be used. Please see `UserSettings`.
   */
  minProgramChainDurationInMin?: number;

  blocked: boolean;
  deleted: boolean;

  email: string;

  syncProjectsWith?: SyncTarget;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  branches?: any;
  targetAppApk?: string;

  /**
   * @deprecated 
   * `comBoxSettings` is deprecated and should not be used. Please see `HyDryDeviceSettings`.
   */
  comBoxSettings?: ComBoxSettings;
  
  projectManagement?: ProjectManagement;

  /**
   * The client's products, one switch each (#2610089), written only by IRES Cloud. IRES Office reads
   * `work` alone: whether the client has IRES Work, which IRES Cloud enforces at sign-in (#2610083).
   * Absent or not `true` means without IRES Work.
   */
  products?: {
    officeDeviceAssignment?: boolean;
    project?: boolean;
    work?: boolean;
    interface?: boolean;
  } | null;

  /**
   * The project types IRES gives this client (#2610081), written only by IRES Cloud. Absent means every
   * type of its products; Einstellungen Projektbearbeitung hides the others.
   */
  allowedProjectTypeIds?: number[] | null;
  
  /**
   * @deprecated 
   * `dryingParameters` is deprecated and should not be used. Please see `ProjectEditingSettings`.
   */
  dryingParameters?: DryingParametersDeprecated;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assignedLicences?: any;

  hyDryMessageSettings?: HyDryMessagesSettings;
  erpSettings?: ErpSettings;

  visibleTo?: string[];

  ignoreIresAdminOrIresClientCheck?: boolean;
  isTestSystem?: boolean;
  isActive?: boolean;
  
  /**
   * @deprecated 
   * `enableGeoEncoding` is deprecated and should not be used. Please see `SystemConfiguration`.
   */
  enableGeoEncoding?: boolean;
  /**
   * @deprecated 
   * `enableVehicleManagement` is deprecated and should not be used. Please see `SystemConfiguration`.
   */
  enableVehicleManagement?: boolean;
  /**
   * @deprecated 
   * `enableScheduling` is deprecated and should not be used. Please see `SystemConfiguration`.
   */
  enableScheduling?: boolean;
  /**
   * @deprecated 
   * `enableAllDevicesManagement` is deprecated and should not be used. Please see `SystemConfiguration`.
   */
  enableAllDevicesManagement?: boolean;
  /**
   * @deprecated 
   * `enableDeviceManagementTile` is deprecated and should not be used. Please see `SystemConfiguration`.
   */
  enableDeviceManagementTile?: boolean;
  /**
   * @deprecated 
   * `enableTimeTracking` is deprecated and should not be used. Please see `SystemConfiguration`.
   */
  enableTimeTracking?: boolean;

  /**
   * @deprecated 
   * `enableMeasurementPlus` is deprecated and should not be used. Please see `ProjectManagementSettings`.
   */
  enableMeasurementPlus?: boolean;
  
  /**
   * @deprecated 
   * `deviceSettings` is deprecated and should not be used. Please see `AllDevicesSettings`.
   */
  deviceSettings?: GenericDeviceSettingsDeprecated;
  timeTrackingConfigurations?: TimeTrackingConfigurations;
  dailyTimeTrackingSettings?: DailyTrackingSettings;

  /**
   * @deprecated 
   * `language` is deprecated and should not be used. Please see `CompanySettings`.
   */
  language?: Language;
  /**
   * @deprecated 
   * `usePrimaryLanguageForReports` is deprecated and should not be used. Please see `CompanySettings`.
   */
  usePrimaryLanguageForReports?: boolean;

  hasAdministrationCapabilities?: boolean;
  memberClientIdForSettings?: string | null;
  isOperationalClient?: boolean;
  
  memberClientIds?: string[];

  memberClientsConfiguration?: MemberClientsConfiguration;
  clientConfiguration?: ClientConfiguration | null;
  administratorClientId?: string | null;

  /**
   * Company email address to be used in reports.
   */
  companyEmailAddress?: string;
  /**
   * The identifier used to recognize a member client should be unique within a group.
   */
  companyIdentifier?: string;

  testMetadata?: ClientTestMetadata | null;
  targetRoomInfoConfigVersion?: string;
}

export interface SyncProjectsWith {
  erpSystems: string[];
  insurancePlatforms: string[];
}

export interface MemberClientDetails {
  id: string;
  clientName: string;
  shortName?: string | null;
  iresProjectNumberPrefix: string;
  email: string;
  zip: string;
  city: string;
  street: string;
  houseNumber: string;
  syncProjectsWith?: SyncProjectsWith | null;
}

/**
 * Creates a default configuration for device settings. This function has been deprecated
 * and should no longer be used in new code. It initializes device settings with all modification
 * permissions disabled, including the creation of new categories or templates, and updates to existing ones.
 * 
 * @deprecated This function is deprecated. Use the new device settings creation functions instead.
 * @returns {GenericDeviceSettingsDeprecated} An object containing default settings with all permissions set to false.
 */
export function createDefaultDeviceSettingsDeprecated() {
  const defaultDeviceSettings: GenericDeviceSettingsDeprecated = {
    newCategoryAllowed: false,
    newTemplateAllowed: false,
    updateAllowed: false,
  };

  return defaultDeviceSettings;
}

export function createrDefaultTimeTrackingConfigurations() {
  const defaultTimeTrackingConfigurations: TimeTrackingConfigurations = {
    specifyStartEnd: false,
    isStartEndMandatory: false,
  };

  return defaultTimeTrackingConfigurations;
}


export function createrDefaultDailyTrackingSettings() {
  const defaultDailyTrackingSettings: DailyTrackingSettings = {
    createDailyTracking: false,
    sendDailyTracking: false,
  };

  return defaultDailyTrackingSettings;
}

export function createDefaultMemberClientsConfiguration() {
  const defaultMemberClientsConfiguration: MemberClientsConfiguration = {
    defineOwnReportSettings: true,
    memberClientsWithOwnReportSettings: [],

    defineOwnRoleSettings: true,
    memberClientsWithOwnRoleSettings: [],

    defineOwnSystemConfiguration: true,
    memberClientsWithOwnSystemConfiguration: [],

    defineOwnUserSettings: true,
    memberClientsWithOwnUserSettings: [],

    defineOwnCompanySettings: true,
    memberClientsWithOwnCompanySettings: [],

    defineOwnHyDryDeviceSettings: true,
    memberClientsWithOwnHyDryDeviceSettings: [],

    defineOwnProjectManagementSettings: true,
    memberClientsWithOwnProjectManagementSettings: [],
  
    defineOwnAllDevicesSettings: true,
    memberClientsWithOwnAllDevicesSettings: [],

    defineOwnHyDryMessagesSettings: true,
    memberClientsWithOwnHyDryMessagesSettings: [],
  }

  return defaultMemberClientsConfiguration;
}

export function createDefaultClientConfiguration() {
  const clientConfiguration: ClientConfiguration = {
    defineOwnReportSettings: true,
    defineOwnRoleSettings: true,
    defineOwnSystemConfiguration: true,
    defineOwnUserSettings: true,
    defineOwnCompanySettings: true,
    defineOwnHyDryDeviceSettings: true,
    defineOwnProjectManagementSettings: true,
    defineOwnAllDevicesSettings: true,
    defineOwnHyDryMessagesSettings: true,
  }

  return clientConfiguration;
}

/**
 * Creates default settings for finished Projects.
 * @returns Default finished projects settings.
 */
export function createDefaultFinishedProjectsSettings() {
  return {
    currentProjectsNotTouched: NumberOfMonths.NEVER,
    deleteProjectsNotProcessed: NumberOfWeeks.NEVER,
    moveFinishedProjects: NumberOfMonths.NEVER,
    moveProjectsClosed: NumberOfWeeks.NEVER,
    moveProjectsDamagePatternClosed: NumberOfWeeks.NEVER,
    moveProjectsOnHold: NumberOfMonths.THREE,
    recentlyFinishedProjects: NumberOfDays.TWO,

    areDefaultFinishedProjectsSettings: true,
  } as FinishedProjectsSettings;
}

/**
 * Creates default system configuration.
 * @returns Default system configuration.
 */
export function createDefaultSystemConfiguration() {
  return {
    enableAllDevicesManagement: false,
    enableDeviceManagementTile: false,
    // Left OFF deliberately. This object is the "nothing is configured" sentinel: every accessor
    // checks `isDefaultSystemConfiguration` and falls back to the client-level flag instead of
    // reading these values, so changing one here would be dead code. Hans's "Init = JA" has no
    // home in this object; see "Init = JA: a migration, deferred" in docs/settings/map-and-tracking.md.
    enableGeoEncoding: false,
    enableTracking: false,
    enableScheduling: false,
    enableTimeTracking: false,
    enableVehicleManagement: false,
    enableDeviceCheckManagement: false,
    enableAiPredictions: false,
    enableBillingCatalogs: false,
    renoActivationScope: RenoActivationScope.ACTIVE_AND_NEW_ONLY,
    enablePersonalChat: false,
    enableProjectChat: false,

    isDefaultSystemConfiguration: true,
  } as SystemConfiguration;
}

/**
 * Function to create default report settings.
 * @returns {ReportSettings} - The default report settings.
 */
export function createDefaultReportSettings() {
  return {
    pdfReportConfig: null,
    reportEmailAddress: {
      damageReportEmailAddress: null,
      dryingReportEmailAddress: null,
      leakDetectionReportEmailAddress: null,
    },
    saveReport: true,
    /**
     * @deprecated
     * `sendReportAsEmail` is deprecated and should not be used. Please see `UserSettings`.
     */
    sendReportAsEmail: false,

    sendReportsAs: SendReportsOption.NO,

    reportsList: ReportsList.SUMMARIZED_REPORTS_ONLY,
    sendReportAsJson: false,

    companyLogo: {
      logoFile: null,
      scaleFactor: 100,
    },

    timeEntriesOnNewPage: true,
    companyAddressInTimeRecord: true,
    workTimeInTimeRecord: false,
    phaseInTimeRecord: true,
    projectDataExchange: ProjectDataExchangeOptions.CSV,
    includeGenericDeviceAssignmentsInReport: false,
    createCertificate: false,
    contactDataInExternalReports: false,

    documentsAsSeparateReport: DocumentsAsSeparateReport.NO,
    timeEntriesAsSeparateReport: TimeEntriesAsSeparateReport.NO,
    measurementProtocolAsSeparateReport: MeasurementProtocolAsSeparateReport.NO,
    // Matches what the two legacy `NO` values above resolve to, so a new client and a client that
    // never opened the setting see the same selection.
    documentsInReports: [ReportInclusion.INTERNAL_REPORT, ReportInclusion.EXTERNAL_REPORT],
    timeEntriesInReports: [ReportInclusion.INTERNAL_REPORT, ReportInclusion.EXTERNAL_REPORT],
    reportFooterText: ReportFooterText.APP_VERSION,
    showTotalPageCount: false,

    areDefaultReportSettings: true,
  } as ReportSettings;
}

/**
 * Function to create default user settings.
 * @returns {UserSettings} - The default user settings.
 */
export function createDefaultUserSettings() {
  return {
    projectSupervisorSource: ProjectSupervisorSource.SUPERVISOR_OF_PROJECT_OWNER,
    strictBranchFilters: false,
    keepProjectOwner: true,
    minProgramChainDurationInMin: 0,
    minGeneralGapDurationInMin: 5,
    // Hans's figure for FAHRT: beyond 200 m from the destination a drive is interrupted, not arrived.
    travelArrivalRadiusInMeters: 200,
    cockpitActiveForAllUsers: true,
    
    areDefaultUserSettings: true,
  } as UserSettings;
}

/**
 * Creates default project settings.
 * @returns Default project settings.
 */
export function createDefaultProjectSettingsDeprecated() {
  return {
    editProjectsIn: ProjectSettingsOptions.BOTH,
    finaliseProjectsIn: ProjectSettingsOptions.BOTH,
    newAppointmentsIn: ProjectSettingsOptions.BOTH,
    newProjectsIn: ProjectSettingsOptions.BOTH,
    
    areDefaultProjectSettings: true,
  } as ProjectSettingsDeprecated;
}

/**
 * Creates default project settings.
 * @returns Default project settings.
 */
export function createDefaultProjectSettingsV2() {
  const defaultOptions = Object.values(ProjectSettingsOptions).filter(
    (option) => option !== ProjectSettingsOptions.BOTH && option !== ProjectSettingsOptions.NEVER
  );

  return {
    editProjectsIn: defaultOptions,
    finaliseProjectsIn: defaultOptions,
    newAppointmentsIn: defaultOptions,
    newProjectsIn: defaultOptions,
    
    areDefaultProjectSettings: true,
  } as ProjectSettingsV2;
}

/**
 * Creates default project devices settings.
 * @returns Default project devices settings.
 */
export function createDefaultProjectDevicesSettings() {
  return {
    enforceMeterReading: false,
    standardIdentification: StandardIdentificationOptions.QR_CODE,
    
    areDefaultProjectDevicesSettings: true,
  } as ProjectDevicesSettings;
}

/**
 * Creates default import configuration.
 * @returns Default import configuration.
 */
export function createDefaultImportConfiguration() {
  return {
    newCategoryAllowed: false,
    newTemplateAllowed: false,
    updateAllowed: false,
    
    defaultImportConfiguration: true,
  } as ImportConfiguration;
}

/**
 * Creates default settings for project editing.
 * @returns Default project editing settings.
 */
export function createDefaultProjectEditingSettings() {
  return {
    defaultDryingDurationInDays: 14,
    defaultFollowUpInspectionIntervalInDays: 7,
    
    areDefaultProjectEditingSettings: true,
  } as ProjectEditingSettings;
}

/**
 * Creates default project manager settings.
 * @returns Default project manager settings.
 */
export function createDefaultProjectManagerSettings() {
  return {
    projectCause: ActionButtonOptions.YELLOW,
    projectAttendees: ActionButtonOptions.RED,
    pipeSystem: ActionButtonOptions.HIDE,
    leakDetectionSketchAndMeasurementPoints: ActionButtonOptions.YELLOW,
    apartmentAttendees: ActionButtonOptions.YELLOW,
    tradeRenovation: ActionButtonOptions.RED,
    
    canAddMeasurementPointsInApartment: false,
    roomClimateMeasuringPoints: ActionButtonOptions.YELLOW,
    measurementPointPresetPhase: MeasurementPointPresetPhase.LEAK_DETECTION,

    enforceFullDamageAssessment: false,
    dryingSetupEnquiry: false,

    sketchInRooms: ActionButtonOptions.YELLOW,
    presetRoomHeight: 240,
    presetInnerWallThickness: 10,
    presetOuterWallThickness: 30,
    presetPartyWallThickness: 30,

    hintPhotoQuality: false,

    tableSorting: TableSortingOptions.PHASE_TRADE,
    tableSortingV2: TableSortingOptionsV2.PHASE_TRADE,
    tableAutomaticallyInReport: true,
    quantitySurveyInReport: false,

    presetFlexibleServiceList: false,
    
    areDefaultProjectManagerSettings: true,
  } as ProjectManagerSettings;
}

/**
 * Creates default device manager settings.
 * @returns Default device manager settings.
 */
export function createDefaultDeviceManagerSettings() {
  return {
    canUserAddProjects: false,
    canUserAddContactPersons: false,
    canUserAddRooms: false,
    canUserAddDevices: false,
    recentlyCompletedTask: 7,
    
    areDefaultDeviceManagerSettings: true,
  } as DeviceManagerSettings;
}

/**
 * Creates default project management settings.
 * @returns Default project management settings.
 */
export function createDefaultProjectManagementSettings() {
  return {
    projectEditingSettings: createDefaultProjectEditingSettings(),
    projectSettings: createDefaultProjectSettingsDeprecated(),
    projectSettingsV2: createDefaultProjectSettingsV2(),
    projectManagerSettings: createDefaultProjectManagerSettings(),
    deviceManagerSettings: createDefaultDeviceManagerSettings(),

    areDefaultProjectManagementSettings: true,
  } as ProjectManagementSettings;
}

/**
 * Function to create default company settings.
 * @returns {CompanySettings} - The default company settings.
 */
export function createDefaultCompanySettings() {
  return {
    language: DEFAULT_LOCALE,
    usePrimaryLanguageForReports: true,
    timezone: DEFAULT_TIMEZONE,

    areDefaultCompanySettings: true,
  } as CompanySettings;
}

/**
 * Creates default settings for a HyDry device.
 * @returns Default HyDry device settings.
 */
export function createDefaultHyDryDeviceSettings() {
  return {
    comBoxClientConfigInfo: undefined,
    minRunDurationForAnalysis: 15,
    alwaysShowSerialNumber: true,

    areDefaultHyDryDeviceSettings: true,
  } as HyDryDeviceSettings;
}

/**
 * Creates default all devices settings.
 * @returns Default all devices settings.
 */
export function createDefaultAllDevicesSettings() {
  return {
    projectDevicesSettings: createDefaultProjectDevicesSettings(),
    importConfiguration: createDefaultImportConfiguration(),

    areDefaultAllDevicesSettings: true,
  } as AllDevicesSettings;
}

export default Client;